import { useState, useEffect } from 'react'
import { useAuth } from '@nyos/shared'
import { useTheme, Theme } from '../contexts/ThemeContext'

interface EntityDeviation {
  entityType: string
  entityId: string
  fieldName: string
  storedValue: number | null
  calculatedValue: number | null
  percentageDeviation: number | null
  recommendedSqlPatch: string
}

interface SpatialDiagnosticResult {
  totalScannedElements: number
  activeDeviations: number
  deviationCountByEntityType: Record<string, number>
  deviations: EntityDeviation[]
}

interface PatchExecutionResponse {
  success: boolean
  message: string
  affectedRows: number
  rollbackTransactionId: string
  entityType: string
  entityId: string
}

export default function SpatialIntegrityHub() {
  const { token, user } = useAuth()
  // Patch path is vendor-only: ADMIN (yard IT) can audit deviations, but
  // reviewing/applying the recommended SQL stays behind SUPERADMIN — the
  // backend enforces the same split on /patch.
  const isSuperAdmin = user?.role === 'ROLE_SUPERADMIN'
  const { theme } = useTheme()
  const [diagnosticResult, setDiagnosticResult] = useState<SpatialDiagnosticResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDeviation, setSelectedDeviation] = useState<EntityDeviation | null>(null)
  const [showPatchModal, setShowPatchModal] = useState(false)
  const [patchResponse, setPatchResponse] = useState<PatchExecutionResponse | null>(null)

  const themeStyles = {
    daylight: {
      card: 'bg-white border border-gray-300',
      text: 'text-gray-900',
      subtext: 'text-gray-600',
      error: 'bg-red-50 border-red-300 text-red-900',
      warning: 'bg-yellow-50 border-yellow-300 text-yellow-900',
      success: 'bg-green-50 border-green-300 text-green-900',
      hover: 'hover:bg-gray-200',
      header: 'bg-gray-200',
      headerText: 'text-gray-800'
    },
    foundry: {
      card: 'bg-zinc-700/50 border border-zinc-300',
      text: 'text-zinc-100',
      subtext: 'text-zinc-400',
      error: 'bg-red-950/30 border-red-500/40 text-red-200',
      warning: 'bg-orange-950/30 border-orange-500/40 text-orange-200',
      success: 'bg-green-950/30 border-green-500/40 text-green-200',
      hover: 'hover:bg-zinc-500/50',
      header: 'bg-zinc-500/100',
      headerText: 'text-zinc-200'
    },
    midnight: {
      card: 'bg-zinc-800/50 border border-zinc-400',
      text: 'text-zinc-100',
      subtext: 'text-zinc-400',
      error: 'bg-transparent border border-red-700/60 text-red-100',
      warning: 'bg-transparent border border-amber-700/60 text-amber-100',
      success: 'bg-transparent border border-green-700/60 text-green-100',
      hover: 'hover:bg-zinc-600/50',
      header: 'bg-zinc-600/100',
      headerText: 'text-zinc-200'
    },
    cyber: {
      card: 'bg-zinc-900 border border-zinc-400',
      text: 'text-zinc-100',
      subtext: 'text-zinc-400',
      error: 'bg-zinc-950 border border-red-400/30 text-red-200 font-mono tracking-wide',
      warning: 'bg-zinc-950 border border-amber-400/30 text-amber-200 font-mono tracking-wide',
      success: 'bg-zinc-950 border border-green-400/30 text-green-200 font-mono tracking-wide',
      hover: 'hover:bg-zinc-500/50',
      header: 'bg-zinc-600/100',
      headerText: 'text-zinc-200'
    }
  }

  const currentStyles = themeStyles[theme as Theme] || themeStyles.foundry

  useEffect(() => {
    fetchDiagnostics()
  }, [])

  const fetchDiagnostics = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('http://localhost:8080/api/v1/admin/diagnostics/spatial/scan', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDiagnosticResult(data)
      }
    } catch (error) {
      console.error('Failed to fetch diagnostics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getDeviationColor = (deviation: number | null) => {
    if (deviation === null) return 'text-gray-500'
    const absDev = Math.abs(deviation)
    if (absDev < 1) return 'text-green-600'
    if (absDev < 2) return 'text-yellow-600'
    return 'text-red-600'
  }

  const groupedDeviations = diagnosticResult?.deviations.reduce((acc, deviation) => {
    if (!acc[deviation.entityType]) {
      acc[deviation.entityType] = []
    }
    acc[deviation.entityType].push(deviation)
    return acc
  }, {} as Record<string, EntityDeviation[]>) || {}

  return (
    <div className="h-[calc(100vh-4rem)] w-full overflow-y-auto p-6 pb-16 custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${currentStyles.text} mb-2`}>Spatial Integrity Hub</h1>
          <p className={currentStyles.subtext}>Admin utility for auditing and correcting spatial data mismatches</p>
        </div>

        {/* Summary Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className={`${currentStyles.card} rounded-lg p-6`}>
            <div className={`text-sm ${currentStyles.subtext} mb-1`}>Total Scanned Elements</div>
            <div className={`text-3xl font-bold ${currentStyles.text}`}>
              {diagnosticResult?.totalScannedElements || 0}
            </div>
          </div>
          <div className={`${currentStyles.card} rounded-lg p-6`}>
            <div className={`text-sm ${currentStyles.subtext} mb-1`}>Active Deviations</div>
            <div className={`text-3xl font-bold ${(diagnosticResult?.activeDeviations || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {diagnosticResult?.activeDeviations || 0}
            </div>
          </div>
          <div className={`${currentStyles.card} rounded-lg p-6`}>
            <div className={`text-sm ${currentStyles.subtext} mb-1`}>By Entity Type</div>
            <div className={`text-lg ${currentStyles.text}`}>
              {Object.entries(diagnosticResult?.deviationCountByEntityType || {}).map(([type, count]) => (
                <div key={type} className="flex justify-between">
                  <span>{type}:</span>
                  <span className={count > 0 ? 'text-red-600' : 'text-green-600'}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={fetchDiagnostics}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Scanning...' : 'Refresh Scan'}
          </button>
        </div>

        {/* Diagnostic Table */}
        {isLoading ? (
          <div className={`${currentStyles.card} rounded-lg p-8 text-center ${currentStyles.subtext}`}>
            Loading diagnostics...
          </div>
        ) : diagnosticResult && Object.keys(groupedDeviations).length > 0 ? (
          <div className="w-full space-y-8 pb-16">
            {Object.entries(groupedDeviations).map(([entityType, deviations]) => (
              <div 
                key={entityType} 
                className={`${currentStyles.card} rounded-lg shadow-md overflow-hidden flex flex-col mb-6`}
              >
                <div className="p-4 border-b border-zinc-700 bg-zinc-900/50 flex-shrink-0">
                  <h2 className={`text-xl font-semibold ${currentStyles.text}`}>
                    {entityType} Deviations ({deviations.length})
                  </h2>
                </div>

                <div className="max-h-[400px] overflow-y-auto overflow-x-auto p-4 custom-scrollbar">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className={`border-b border-zinc-700 sticky top-0 z-10 ${currentStyles.header}`}>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Entity ID</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Field</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Stored Value</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Calculated Value</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Deviation</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${currentStyles.headerText} uppercase tracking-wider`}>Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-700">
                      {deviations.map((deviation, index) => (
                        <tr key={`${deviation.entityId}-${index}`} className={`${currentStyles.hover} transition-colors`}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentStyles.text} font-mono`}>
                            {deviation.entityId}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentStyles.text}`}>
                            {deviation.fieldName}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentStyles.text}`}>
                            {deviation.storedValue !== null ? deviation.storedValue.toFixed(2) : 'N/A'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentStyles.text}`}>
                            {deviation.calculatedValue !== null ? deviation.calculatedValue.toFixed(2) : 'N/A'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${getDeviationColor(deviation.percentageDeviation)}`}>
                            {deviation.percentageDeviation !== null ? `${deviation.percentageDeviation.toFixed(2)}%` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {deviation.recommendedSqlPatch && !deviation.recommendedSqlPatch.startsWith('Foul zone') && (
                              isSuperAdmin ? (
                                <button
                                  onClick={() => {
                                    setSelectedDeviation(deviation)
                                    setShowPatchModal(true)
                                    setPatchResponse(null)
                                  }}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                                >
                                  Review Patch
                                </button>
                              ) : (
                                <span className={`text-xs ${currentStyles.subtext}`}>Vendor Review Required</span>
                              )
                            )}
                            {deviation.recommendedSqlPatch?.startsWith('Foul zone') && (
                              <span className={`text-xs ${currentStyles.subtext}`}>Manual Review Required</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`${currentStyles.card} rounded-lg p-8 text-center ${currentStyles.subtext}`}>
            No spatial deviations detected. All geometry data is consistent.
          </div>
        )}

        {/* Patch Review Modal */}
        {showPatchModal && selectedDeviation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className={`${currentStyles.card} rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-xl font-semibold ${currentStyles.text}`}>Review & Apply SQL Patch</h2>
                  <button
                    onClick={() => {
                      setShowPatchModal(false)
                      setSelectedDeviation(null)
                      setPatchResponse(null)
                    }}
                    className={`text-${currentStyles.subtext} hover:text-white`}
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Entity Type</label>
                    <div className={`text-sm ${currentStyles.text}`}>{selectedDeviation.entityType}</div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Entity ID</label>
                    <div className={`text-sm ${currentStyles.text} font-mono`}>{selectedDeviation.entityId}</div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Field</label>
                    <div className={`text-sm ${currentStyles.text}`}>{selectedDeviation.fieldName}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Current Value</label>
                      <div className={`text-sm ${currentStyles.text}`}>
                        {selectedDeviation.storedValue !== null ? selectedDeviation.storedValue.toFixed(2) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Corrected Value</label>
                      <div className={`text-sm ${currentStyles.text} font-semibold`}>
                        {selectedDeviation.calculatedValue !== null ? selectedDeviation.calculatedValue.toFixed(2) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  {selectedDeviation.entityType !== 'FOUL_ZONE' && selectedDeviation.storedValue !== null && selectedDeviation.calculatedValue !== null && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={`${currentStyles.card} p-4 rounded-lg`}>
                        <h3 className={`text-sm font-semibold ${currentStyles.text} mb-2`}>Option A: Update Metadata Length</h3>
                        <p className={`text-xs ${currentStyles.subtext} mb-3`}>Use this if the physical map is correct, but the database text column is outdated.</p>
                        <pre className={`${currentStyles.card} p-3 rounded text-xs font-mono overflow-x-auto ${currentStyles.text} mb-3`}>
                          {`UPDATE tracks SET ${selectedDeviation.fieldName} = ${(selectedDeviation.calculatedValue ?? 0).toFixed(2)} WHERE id = '${selectedDeviation.entityId}';`}
                        </pre>
                        <button
                          onClick={() => navigator.clipboard.writeText(`UPDATE tracks SET ${selectedDeviation.fieldName} = ${(selectedDeviation.calculatedValue ?? 0).toFixed(2)} WHERE id = '${selectedDeviation.entityId}';`)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                        >
                          Copy SQL
                        </button>
                      </div>
                      {selectedDeviation.storedValue > selectedDeviation.calculatedValue && (
                        <div className={`${currentStyles.card} p-4 rounded-lg`}>
                          <h3 className={`text-sm font-semibold ${currentStyles.text} mb-2`}>Option B: Extend Map Geometry</h3>
                          <p className={`text-xs ${currentStyles.subtext} mb-3`}>Use this if the track is physically longer in the yard and the map line was drawn incomplete.</p>
                          <pre className={`${currentStyles.card} p-3 rounded text-xs font-mono overflow-x-auto ${currentStyles.text} mb-3`}>
                            {`WITH extended AS (
  SELECT 
    id,
    ST_SetPoint(
      geometry,
      1, -- Target the second vertex (0-indexed for ST_SetPoint)
      ST_Project(
        ST_StartPoint(geometry)::geography, -- Project FROM the first point (safest anchor)
        ${(((selectedDeviation.storedValue ?? 0) - (selectedDeviation.calculatedValue ?? 0)) * 0.3048).toFixed(2)}, -- Distance in meters
        CASE 
          -- If the angle is more than 45° from east, force it dead East
          WHEN abs(ST_Azimuth(ST_PointN(geometry, 1), ST_PointN(geometry, 2)) - radians(90)) > radians(45)
          THEN radians(90)
          
          -- Otherwise, maintain the original natural vector heading
          ELSE ST_Azimuth(ST_PointN(geometry, 1), ST_PointN(geometry, 2))
        END
      )::geometry
    ) as extended_geometry
  FROM tracks
  WHERE id = '${selectedDeviation.entityId}'
)
UPDATE tracks 
SET geometry = extended_geometry 
FROM extended 
WHERE tracks.id = extended.id;`}
                          </pre>
                          <button
                            onClick={() => {
                              const sql = `WITH extended AS (
  SELECT 
    id,
    ST_SetPoint(
      geometry,
      1, -- Target the second vertex (0-indexed for ST_SetPoint)
      ST_Project(
        ST_StartPoint(geometry)::geography, -- Project FROM the first point (safest anchor)
        ${(((selectedDeviation.storedValue ?? 0) - (selectedDeviation.calculatedValue ?? 0)) * 0.3048).toFixed(2)}, -- Distance in meters
        CASE 
          -- If the angle is more than 45° from east, force it dead East
          WHEN abs(ST_Azimuth(ST_PointN(geometry, 1), ST_PointN(geometry, 2)) - radians(90)) > radians(45)
          THEN radians(90)
          
          -- Otherwise, maintain the original natural vector heading
          ELSE ST_Azimuth(ST_PointN(geometry, 1), ST_PointN(geometry, 2))
        END
      )::geometry
    ) as extended_geometry
  FROM tracks
  WHERE id = '${selectedDeviation.entityId}'
)
UPDATE tracks 
SET geometry = extended_geometry 
FROM extended 
WHERE tracks.id = extended.id;`
                              navigator.clipboard.writeText(sql)
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                          >
                            Copy SQL
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedDeviation.entityType === 'FOUL_ZONE' && selectedDeviation.recommendedSqlPatch && (
                    <div>
                      <label className={`block text-sm font-medium ${currentStyles.subtext} mb-1`}>Recommendation</label>
                      <pre className={`${currentStyles.card} p-3 rounded text-xs font-mono overflow-x-auto ${currentStyles.text}`}>
                        {selectedDeviation.recommendedSqlPatch}
                      </pre>
                    </div>
                  )}

                  {patchResponse && (
                    <div className={`p-4 rounded-md ${patchResponse.success ? currentStyles.success : currentStyles.error}`}>
                      <div className="font-semibold mb-1">
                        {patchResponse.success ? '✓ Patch Applied Successfully' : '✗ Patch Failed'}
                      </div>
                      <div className="text-sm">{patchResponse.message}</div>
                      {patchResponse.success && (
                        <div className="text-sm mt-2">
                          Affected Rows: {patchResponse.affectedRows} | Transaction ID: {patchResponse.rollbackTransactionId}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <button
                      onClick={() => {
                        setShowPatchModal(false)
                        setSelectedDeviation(null)
                        setPatchResponse(null)
                      }}
                      className="px-4 py-2 border border-zinc-600 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
